/**
 * The navigation model: every backend-declared shell destination plus the
 * client-side surfaces (workviews, settings, account, design gallery),
 * grouped the way the legacy studio grouped panes. Single source for the
 * AppShell sidebar and the command palette.
 */
export interface StudioDestination {
  id: string;
  label: string;
  /** Route path (typed route ids come from the generated route tree). */
  to: string;
  /** Lucide icon name rendered by the shell (kept as a key, not a node, so this module stays view-free). */
  icon:
    | "home"
    | "activity"
    | "bot"
    | "cloud"
    | "network"
    | "layers"
    | "database"
    | "target"
    | "zap"
    | "radio"
    | "inbox"
    | "git-branch"
    | "calendar-clock"
    | "archive"
    | "terminal"
    | "scale"
    | "search"
    | "list-checks"
    | "settings"
    | "user"
    | "palette";
}

export interface StudioNavGroupConfig {
  id: string;
  label: string;
  items: ReadonlyArray<StudioDestination>;
}

export const STUDIO_NAV_GROUPS: ReadonlyArray<StudioNavGroupConfig> = [
  {
    id: "studio",
    label: "Studio",
    items: [
      { id: "overview", label: "Overview", to: "/", icon: "home" },
      { id: "activity", label: "Activity", to: "/activity", icon: "activity" },
      { id: "agents", label: "Agents", to: "/agents", icon: "bot" },
      { id: "cloud", label: "Cloud", to: "/cloud", icon: "cloud" },
      { id: "ontology", label: "Ontology", to: "/ontology", icon: "network" },
    ],
  },
  {
    id: "inspect",
    label: "Inspect",
    items: [
      { id: "observations", label: "Observations", to: "/observations", icon: "inbox" },
      { id: "facts", label: "Facts", to: "/facts", icon: "database" },
      { id: "intents", label: "Intents", to: "/intents", icon: "target" },
      { id: "effects", label: "Effects", to: "/effects", icon: "zap" },
      { id: "events", label: "Events", to: "/events", icon: "radio" },
      { id: "projections", label: "Projections", to: "/projections", icon: "layers" },
      { id: "provenance", label: "Provenance", to: "/provenance", icon: "git-branch" },
      { id: "workviews", label: "Workviews", to: "/workviews", icon: "list-checks" },
    ],
  },
  {
    id: "operate",
    label: "Operate",
    items: [
      { id: "schedules", label: "Schedules", to: "/schedules", icon: "calendar-clock" },
      { id: "storage", label: "Storage", to: "/storage", icon: "archive" },
      { id: "commands", label: "Commands", to: "/commands", icon: "terminal" },
      { id: "reconcile", label: "Reconcile", to: "/reconcile", icon: "scale" },
      { id: "query", label: "Query", to: "/query", icon: "search" },
      { id: "settings", label: "Settings", to: "/settings", icon: "settings" },
      { id: "account", label: "Account", to: "/account", icon: "user" },
    ],
  },
] as const;

export const DESIGN_GALLERY_DESTINATION: StudioDestination = {
  id: "design",
  label: "Design gallery",
  to: "/design",
  icon: "palette",
};

export function allDestinations(): StudioDestination[] {
  return [...STUDIO_NAV_GROUPS.flatMap((group) => [...group.items]), DESIGN_GALLERY_DESTINATION];
}
